export type Role = 'viewer' | 'triager' | 'superAdmin';

export const RANK: Record<Role, number> = {
  viewer: 0,
  triager: 1,
  superAdmin: 2,
};

export function hasRole(actual: Role | null | undefined, min: Role): boolean {
  if (actual == null) return false;
  return RANK[actual] >= RANK[min];
}

export function isValidRole(s: string): s is Role {
  return s === 'viewer' || s === 'triager' || s === 'superAdmin';
}

export const ALLOWED_GRANT_DOMAINS = ['rewind.io', 'rewind.com'] as const;

// Two unambiguous runs separated by a literal `@` (excluded from both
// character classes), so matching is linear. The domain's `.` requirement is
// enforced separately below to avoid the polynomial-backtracking ambiguity of
// a `\.` inside a repeated class (js/polynomial-redos).
const EMAIL_RE = /^[^\s@]+@[^\s@]+$/;

export type GrantEmailResult =
  | { ok: true; email: string }
  | { ok: false; error: 'invalid_email'; message: string };

export function validateGrantEmail(raw: string): GrantEmailResult {
  if (typeof raw !== 'string' || !EMAIL_RE.test(raw.trim())) {
    return { ok: false, error: 'invalid_email', message: 'Not a valid email address.' };
  }
  const lower = raw.trim().toLowerCase();
  const domain = lower.split('@')[1] ?? '';
  if (!domain.includes('.')) {
    return { ok: false, error: 'invalid_email', message: 'Not a valid email address.' };
  }
  if (!ALLOWED_GRANT_DOMAINS.includes(domain as (typeof ALLOWED_GRANT_DOMAINS)[number])) {
    return {
      ok: false,
      error: 'invalid_email',
      message: `Email domain must be one of: ${ALLOWED_GRANT_DOMAINS.join(', ')}.`,
    };
  }
  return { ok: true, email: lower };
}
